import { useMemo } from "react";
import type { StonePattern } from "../../../shared/api-contract";
import { resolveStonePatternLayer } from "../../../shared/api-contract";
import { Slider, Toggle } from "../../kit";
import { useRing } from "../ringContext";
import { LAYER_SECTIONS, layerControlHandler, layerSummary, visibleControls } from "../patternUi";
import type { LayerCtl, LayerCtx, LayerSection } from "../patternUi";

const SECTION_TITLES: Record<LayerSection, string> = {
  Shape: "Shape and place",
  Length: "Length and count",
  Stones: "Stone sizes",
  Spacing: "Spacing",
  Setting: "Setting",
  Recess: "Recess",
  Lines: "Lines beside the stones",
  Copies: "Copies and mirror",
};

/**
 * One layer of a stone pattern. Rendered entirely from `LAYER_CONTROLS`
 * (patternUi.ts) — every control writes through `layerControlHandler`, which
 * sends the whole pattern with `applyRingDesign`. Chips go through `apply`;
 * sliders are kit sliders, so a drag is draft-batched like every other row.
 */
export function LayerCard({
  pattern,
  index,
  lc,
  open,
  onToggle,
  onRemove,
}: {
  pattern: StonePattern;
  index: number;
  lc: LayerCtx;
  open: boolean;
  onToggle: () => void;
  onRemove?: () => void;
}) {
  const { r, apply } = useRing();
  const raw = pattern.layers[index] ?? {};
  const L = useMemo(() => resolveStonePatternLayer(raw), [raw]);
  const send = layerControlHandler({ r, apply }, pattern, index, lc);
  const controls = visibleControls(raw);

  return (
    <div
      data-layer={index}
      className={`flex flex-col rounded-xl border bg-white ${open ? "border-ed-accent/60 shadow-[0_2px_10px_rgba(26,26,25,0.06)]" : "border-ed-border"}`}
    >
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex min-w-0 flex-1 flex-col text-left">
          <span className="font-body text-[12.5px] font-semibold text-ed-text">Layer {index + 1}</span>
          <span className="truncate font-body text-[11px] text-ed-text-2">{layerSummary(raw, lc)}</span>
        </button>
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            title="Remove this layer"
            className="rounded-md px-2 py-1 font-body text-[11px] text-ed-text-2 hover:bg-ed-hover hover:text-ed-red"
          >
            Remove
          </button>
        ) : null}
        <button
          type="button"
          onClick={onToggle}
          aria-label={open ? "Fold layer" : "Open layer"}
          className="flex h-7 w-7 items-center justify-center rounded-md font-body text-[15px] text-ed-text-2 hover:bg-ed-hover"
        >
          {open ? "–" : "+"}
        </button>
      </div>

      {open ? (
        <div className="flex flex-col gap-4 border-t border-ed-border px-3 pb-4 pt-3">
          {LAYER_SECTIONS.map((section) => {
            const rows = controls.filter((c) => c.section === section);
            if (!rows.length) return null;
            return (
              <div key={section} className="flex flex-col gap-3">
                <span className="font-body text-[11px] font-semibold uppercase tracking-[0.08em] text-ed-text-3">
                  {SECTION_TITLES[section]}
                </span>
                {rows.map((ctl) => (
                  <ControlRow key={ctl.key} ctl={ctl} raw={raw} L={L} lc={lc} send={send} />
                ))}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function ControlRow({
  ctl,
  raw,
  L,
  lc,
  send,
}: {
  ctl: LayerCtl;
  raw: StonePattern["layers"][number];
  L: ReturnType<typeof resolveStonePatternLayer>;
  lc: LayerCtx;
  send: ReturnType<typeof layerControlHandler>;
}) {
  const hint = ctl.hint ? <span className="font-body text-[11px] leading-snug text-ed-text-2">{ctl.hint}</span> : null;

  if (ctl.kind === "chips") {
    const cur = ctl.read(L, raw, lc);
    return (
      <div className="flex flex-col gap-1.5" data-ctl={ctl.key}>
        <span className="font-body text-[11.5px] font-medium text-ed-text">{ctl.label}</span>
        <div className="flex flex-wrap gap-1.5">
          {ctl.options
            // "Custom" length is shown only while it is the live state.
            .filter((o) => o.id !== "custom" || cur === "custom")
            .map((o) => (
              <button
                key={o.id}
                type="button"
                aria-pressed={cur === o.id}
                onClick={() => { if (cur !== o.id) void send(ctl, o.id); }}
                className={`rounded-md border px-2.5 py-1.5 font-body text-[11.5px] transition-colors ${
                  cur === o.id
                    ? "border-ed-accent bg-ed-active font-medium text-ed-text"
                    : "border-ed-border bg-white text-ed-text-2 hover:border-ed-line hover:text-ed-text"
                }`}
              >
                {o.label}
              </button>
            ))}
        </div>
        {hint}
      </div>
    );
  }

  if (ctl.kind === "toggle") {
    const on = ctl.read(L, raw, lc);
    return (
      <div className="flex flex-col gap-1" data-ctl={ctl.key}>
        <div className="flex items-center justify-between gap-3">
          <span className="font-body text-[11.5px] font-medium text-ed-text">{ctl.label}</span>
          <Toggle label={ctl.label} checked={on} onChange={(v) => void send(ctl, v)} />
        </div>
        {hint}
      </div>
    );
  }

  const min = ctl.min(lc);
  const max = ctl.max(lc);
  const value = Math.max(min, Math.min(max, ctl.read(L, raw, lc)));
  return (
    <div className="flex flex-col gap-1" data-ctl={ctl.key}>
      <Slider
        label={ctl.label}
        min={min}
        max={max}
        step={ctl.step}
        value={value}
        unit={ctl.unit}
        display={ctl.display ? (v) => ctl.display!(v, lc, L) : undefined}
        onChange={(v) => void send(ctl, v)}
      />
      {hint}
    </div>
  );
}
